import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import FrequencySelect from 'components/FrequencySelect';
import AnalyzerSelectLocal from 'features/analyzers/AnalyzerSelectLocal';
import { useSwitches } from 'features/switches/api/getSwitches';
import SwitchSelectLocal from 'features/switches/SwitchSelectLocal';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormWatch,
} from 'react-hook-form';
import { useAnalyzers } from '../analyzers/api/getAnalyzers';
import { AnalyzerDishForm } from './types';

interface Props {
  watch: UseFormWatch<AnalyzerDishForm>;
  control: Control<AnalyzerDishForm>;
  errors: FieldErrors<AnalyzerDishForm>;
  toggleEntity: () => void;
}
const PairAnalyzerDishForm = ({
  watch,
  control,
  errors,
  toggleEntity,
}: Props) => {
  const { data: analyzers } = useAnalyzers({});
  const { data: switches } = useSwitches({});

  const selectedAnalyzer = analyzers?.find(
    (analyzer) => analyzer.id === Number(watch('analyzer'))
  );
  const selectedSwitch = switches?.find(
    (switchItem) => switchItem.id === Number(watch('switch'))
  );

  let maxPorts: number | undefined | null = 32;
  if (watch('selectedEntity') === 'analyzer')
    maxPorts = selectedAnalyzer?.number_of_ports;
  else if (watch('selectedEntity') === 'switch')
    maxPorts = selectedSwitch?.number_of_ports;

  return (
    <Grid
      container
      spacing={1}
      direction='column'
      style={{ paddingTop: '.4rem' }}
    >
      <Grid item container alignItems='center' xs spacing={1}>
        <Grid item xs>
          {watch('selectedEntity') === 'analyzer' ? (
            <Controller
              name='analyzer'
              control={control}
              rules={{
                required: true,
                validate: (value) =>
                  (Number(value) !== -1 && !!value) ||
                  'Analyzer must be selected.',
              }}
              render={({ field: { onChange, value } }) => (
                <AnalyzerSelectLocal
                  analyzers={analyzers}
                  selectedAnalyzerId={Number(value)}
                  handleAnalyzerChanged={onChange}
                  fullWidth
                  error={!!errors.analyzer}
                  required
                />
              )}
            />
          ) : (
            <Controller
              name='switch'
              control={control}
              rules={{
                required: true,
                validate: (value) =>
                  (Number(value) !== -1 && !!value) ||
                  'Switch must be selected.',
              }}
              render={({ field: { onChange, value } }) => (
                <SwitchSelectLocal
                  switches={switches}
                  selectedSwitchId={Number(value)}
                  handleSwitchChanged={onChange}
                  fullWidth
                  error={!!errors.switch}
                  required
                />
              )}
            />
          )}
        </Grid>
        <Grid item>
          <Button variant='contained' onClick={toggleEntity}>
            Connect{' '}
            {watch('selectedEntity') === 'switch' ? 'Analyzer' : 'Switch'}
          </Button>
        </Grid>
      </Grid>
      <Grid item xs>
        <Controller
          name='feed_name'
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              id='feed_name'
              label='Feed Name'
              name='feed_name'
              value={value}
              onChange={onChange}
              fullWidth
              error={!!errors.feed_name}
            />
          )}
        />
      </Grid>
      <Grid item xs>
        <Controller
          name='port'
          control={control}
          rules={{
            required: true,
            min: 1,
            validate: (value) =>
              (!!value && Number(value) <= (maxPorts ? maxPorts : 16)) ||
              'Port value is invalid.',
          }}
          render={({ field: { onChange, value } }) => (
            <TextField
              id='port'
              label='Port'
              name='port'
              value={value}
              onChange={onChange}
              fullWidth
              type='number'
              required
              inputProps={{
                max: maxPorts,
                min: 1,
              }}
              error={!!errors.port}
            />
          )}
        />
      </Grid>
      <Grid item xs>
        <Controller
          name='polarity'
          control={control}
          rules={{
            required: false,
          }}
          render={({ field: { onChange, value } }) => (
            <FormControl fullWidth>
              <InputLabel id='polarity-select-label'>Polarity</InputLabel>
              <Select
                labelId='polarity-select-label'
                label='Polarity'
                id='polarity'
                name='polarity'
                onChange={onChange}
                value={value}
              >
                <MenuItem key='0' value={''}>
                  None
                </MenuItem>
                <MenuItem key='Vertical' value={'Vertical'}>
                  Vertical
                </MenuItem>
                <MenuItem key='Horizontal' value={'Horizontal'}>
                  Horizontal
                </MenuItem>
                <MenuItem key='RHCP' value={'RHCP'}>
                  RHCP
                </MenuItem>
                <MenuItem key='LHCP' value={'LHCP'}>
                  LHCP
                </MenuItem>
              </Select>
            </FormControl>
          )}
        />
      </Grid>
      <Grid item xs>
        <Controller
          name='link_type'
          control={control}
          rules={{
            required: false,
          }}
          render={({ field: { onChange, value } }) => (
            <FormControl fullWidth>
              <InputLabel id='link-select-label'>Link Type</InputLabel>
              <Select
                labelId='link-select-label'
                label='Link Type'
                id='link_type'
                name='link_type'
                onChange={onChange}
                value={value}
              >
                <MenuItem key='0' value={''}>
                  None
                </MenuItem>
                <MenuItem key='Downlink' value={'Downlink'}>
                  Downlink
                </MenuItem>
                <MenuItem key='Uplink' value={'Uplink'}>
                  Uplink
                </MenuItem>
              </Select>
            </FormControl>
          )}
        />
      </Grid>
      <Grid container item spacing={1}>
        <Grid item xs>
          <Controller
            name='loValue'
            control={control}
            rules={{
              required: false,
              validate: (value) => Number(value) >= 0 || 'LO must be positive',
            }}
            render={({ field: { onChange, value } }) => (
              <TextField
                variant='outlined'
                fullWidth
                type='number'
                id='loValue'
                label='LO'
                name='loValue'
                autoComplete='lo'
                value={value}
                onChange={onChange}
                error={!!errors.loValue}
              />
            )}
          />
        </Grid>
        <Grid item xs>
          <Controller
            name='loUnit'
            control={control}
            rules={{
              required: false,
            }}
            render={({ field: { onChange, value } }) => (
              <FrequencySelect
                selected={value}
                name='loUnit'
                onChange={onChange}
                large
              />
            )}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PairAnalyzerDishForm;
